import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

function isRegulationUrl(value: string): boolean {
  return /^https?:\/\//i.test(value.trim());
}

export interface SpaceTermsAcceptanceProps {
  /** Regulation as full text or URL. When empty/missing, nothing is rendered. */
  regulation?: string;
  /** Display name of the space for the T&C link label. */
  spaceName?: string;
  accepted: boolean;
  onAcceptedChange: (accepted: boolean) => void;
}

/**
 * Quiet legal acceptance for booking: checkbox with an inline link to view
 * the space's T&C. Avoids a competing primary-style CTA next to Book/Next.
 */
export default function SpaceTermsAcceptance({
  regulation,
  spaceName,
  accepted,
  onAcceptedChange,
}: SpaceTermsAcceptanceProps) {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);

  const trimmed = regulation?.trim();
  if (!trimmed) {
    return null;
  }

  const isUrl = isRegulationUrl(trimmed);
  const resolvedSpaceName = spaceName?.trim();
  const termsLabel = resolvedSpaceName
    ? t('bookingSteps.spaceTermsNamed', `${resolvedSpaceName} T&C`, { spaceName: resolvedSpaceName })
    : t('bookingSteps.spaceTerms', 'T&C');

  const openTerms = (event: React.MouseEvent) => {
    // Keep the checkbox from toggling when the link is clicked.
    event.preventDefault();
    event.stopPropagation();
    if (isUrl) {
      window.open(trimmed, '_blank', 'noopener,noreferrer');
      return;
    }
    setDialogOpen(true);
  };

  const handleAgree = () => {
    onAcceptedChange(true);
    setDialogOpen(false);
  };

  return (
    <Box>
      <FormControlLabel
        sx={{
          alignItems: 'center',
          mx: 0,
          gap: 0.75,
          '& .MuiCheckbox-root': {
            p: 0.5,
          },
          '& .MuiFormControlLabel-label': {
            pt: 0,
          },
        }}
        control={
          <Checkbox
            size="small"
            checked={accepted}
            onChange={(e) => onAcceptedChange(e.target.checked)}
          />
        }
        label={
          <Typography
            variant="caption"
            component="span"
            color="text.secondary"
            sx={{ display: 'inline', lineHeight: 1.4 }}
          >
            {t('bookingSteps.acceptTermsLead', 'I agree to the')}{' '}
            <Link
              component="button"
              type="button"
              variant="caption"
              onClick={openTerms}
              sx={{
                display: 'inline',
                p: 0,
                m: 0,
                verticalAlign: 'baseline',
                font: 'inherit',
                fontWeight: 600,
                lineHeight: 'inherit',
                textDecoration: 'underline',
                textUnderlineOffset: 2,
                cursor: 'pointer',
              }}
            >
              {termsLabel}
            </Link>
          </Typography>
        }
      />

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="space-terms-dialog-title"
      >
        <DialogTitle id="space-terms-dialog-title">{termsLabel}</DialogTitle>
        <DialogContent dividers>
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'pre-wrap',
              maxHeight: 360,
              overflow: 'auto',
            }}
          >
            {trimmed}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setDialogOpen(false)} color="inherit">
            {t('common.close', 'Close')}
          </Button>
          <Button variant="contained" onClick={handleAgree}>
            {t('bookingSteps.agreeTerms', 'Agree')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
